// src/components/ProductionDashboard/OrdersInbox.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productionApi } from '../api';

const priorityColor = (p) => {
  if (p === 1) return { bg: 'bg-red-100', text: 'text-red-700', label: 'URGENT' };
  if (p === 2) return { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH' };
  if (p === 4) return { bg: 'bg-lime-100', text: 'text-lime-700', label: 'LOW' };
  if (p === 5) return { bg: 'bg-slate-100', text: 'text-slate-700', label: 'VERY LOW' };
  return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'NORMAL' };
};

const deadlineClass = (days) => {
  if (days === null || days === undefined) return 'bg-slate-500';
  if (days < 3) return 'bg-red-500';
  if (days < 7) return 'bg-orange-400';
  return 'bg-green-600';
};

const FILTERS = [
  { value: 'SENT_TO_PRODUCTION,IN_PRODUCTION', label: 'All Active' },
  { value: 'SENT_TO_PRODUCTION', label: 'Awaiting Review' },
  { value: 'IN_PRODUCTION', label: 'In Production' },
];

export default function OrdersInbox() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('SENT_TO_PRODUCTION');
  const [awaitingReviewCount, setAwaitingReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    productionApi
      .get(`/sales/orders/production-inbox?status=${filter}`)
      .then((r) => {
        const sortedOrders = [...(r.data.data || [])].sort((a, b) => {
          const priorityA = Number.isFinite(Number(a.priority)) ? Number(a.priority) : 3;
          const priorityB = Number.isFinite(Number(b.priority)) ? Number(b.priority) : 3;
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          const dateA = a.expected_delivery ? new Date(a.expected_delivery).getTime() : Number.MAX_SAFE_INTEGER;
          const dateB = b.expected_delivery ? new Date(b.expected_delivery).getTime() : Number.MAX_SAFE_INTEGER;
          return dateA - dateB;
        });
        setOrders(sortedOrders);
        setAwaitingReviewCount(r.data.awaiting_review_count || 0);
      })
      .catch(() => setError('Failed to load orders inbox.'))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/production')}
            className="text-slate-400 hover:text-slate-700 transition-colors text-sm font-medium"
          >
            ← Dashboard
          </button>
          <span className="text-slate-300">/</span>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Orders Inbox</h1>
          {awaitingReviewCount > 0 && (
            <span className="bg-red-500 text-white rounded-full px-3 py-1 text-sm font-bold">
              {awaitingReviewCount} awaiting review
            </span>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
              filter === f.value
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center py-24 text-slate-400 text-sm font-medium">
          Loading orders…
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 text-sm font-medium">
          {error}
        </div>
      )}
      {!loading && !error && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-2">
          <span className="text-4xl">📭</span>
          <span className="text-sm font-medium">No orders found for this filter.</span>
        </div>
      )}

      {/* Order cards */}
      {!loading &&
        !error &&
        orders.map((order) => {
          const pc = priorityColor(order.priority);
          return (
            <div
              key={order.order_id}
              className="bg-white border border-slate-200 rounded-2xl mb-4 overflow-hidden shadow-sm"
            >
              {/* Card header */}
              <div className="bg-slate-900 text-white px-5 py-3 flex justify-between items-center">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-bold text-lg">{order.order_code}</span>
                 
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-black ${pc.bg} ${pc.text}`}
                  >
                    {pc.label}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      order.status === 'SENT_TO_PRODUCTION'
                        ? 'bg-violet-100 text-violet-700'
                        : order.status === 'CONFIRMED'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {order.status}
                  </span>
                </div>
                <div
                  className={`text-white text-sm font-bold px-3 py-1 rounded-xl ${deadlineClass(
                    order.days_remaining
                  )}`}
                >
                  {order.days_remaining !== null && order.days_remaining !== undefined
                    ? `${order.days_remaining} days left`
                    : 'No deadline'}
                </div>
              </div>

              {/* Order meta */}
              <div className="px-5 pt-3 pb-1 flex gap-6 text-xs text-slate-500 border-b border-slate-100">
                {order.expected_delivery && (
                  <span>
                    <span className="font-bold text-slate-700">Delivery:</span>{' '}
                    {order.expected_delivery}
                  </span>
                )}
                <span>
                  <span className="font-bold text-slate-700">Total Qty:</span> {order.total_qty}
                </span>
                {order.special_instructions && (
                  <span>
                    <span className="font-bold text-slate-700">Instructions:</span>{' '}
                    {order.special_instructions}
                  </span>
                )}
              </div>

              {/* Items table */}
              <div className="p-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 text-xs uppercase">
                      <th className="text-left py-2 pr-3">Design Code</th>
                      <th className="text-left py-2 pr-3">Design Name</th>
                      <th className="text-center py-2 pr-3">Qty</th>
                      <th className="text-left py-2 pr-3">Special Req.</th>
                      <th className="text-left py-2 pr-3">Job Card / Progress</th>
                      <th className="text-right py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr key={item.item_id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-3 font-mono text-indigo-700 font-bold text-xs">
                          {item.design_code}
                        </td>
                        <td className="py-2 pr-3 text-slate-700">{item.design_name}</td>
                        <td className="py-2 pr-3 text-center font-bold text-slate-900">
                          {item.quantity}
                        </td>
                        <td className="py-2 pr-3 text-slate-500 text-xs">
                          {item.special_requirements || '—'}
                        </td>
                        <td className="py-2 pr-3">
                          {item.job_card_progress ? (
                            <div>
                              <div className="text-xs text-slate-600 font-bold mb-1">
                                {item.job_card_progress.job_card_no} —{' '}
                                {item.job_card_progress.pct}% complete
                              </div>
                              <div className="w-36 bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-indigo-500 h-2 rounded-full transition-all"
                                  style={{ width: `${item.job_card_progress.pct}%` }}
                                />
                              </div>
                              {item.job_card_progress.current_stage && (
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  Current: {item.job_card_progress.current_stage}
                                </div>
                              )}
                              <div className="text-[10px] text-slate-400">
                                {item.job_card_progress.stages_done}/
                                {item.job_card_progress.stages_total} stages done
                              </div>
                            </div>
                          ) : (
                            <span className="text-orange-500 text-xs font-medium">
                              {order.status === 'SENT_TO_PRODUCTION' ? 'Awaiting review' : 'No job card yet'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          {item.job_card_id ? (
                            <button
                              onClick={() =>
                                navigate(`/production/job-cards/${item.job_card_id}`)
                              }
                              className="text-xs text-indigo-600 font-bold hover:text-indigo-800 underline"
                            >
                              View JC
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                navigate(`/production/job-card?order_id=${encodeURIComponent(order.order_code)}&sales_order_id=${order.order_id}&design_id=${item.design_id}&qty=${item.quantity}&customer_id=${order.customer_id || ''}&expected_delivery_date=${encodeURIComponent(order.expected_delivery || '')}&remarks=${encodeURIComponent(item.special_requirements || order.special_instructions || '')}`)
                              }
                              className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg font-bold transition-colors"
                            >
                              Create Job Card
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
    </div>
  );
}
